/* eslint-disable jsdoc/no-undefined-types */
import cartData from '#shared/util/cartData.js';
import invalidCartRedirectMiddleware from '#shared/middleware/invalidCartRedirectMiddleware.js';
import loadOrderMiddleware from '#shared/middleware/loadOrderMiddleware.js';
import paymentReturnMiddleware from '#shared/middleware/paymentReturnMiddleware.js';

import cBillingSummary from '#themes/default/default/components/billingSummary/billingSummary.js';
import cButton from '#themes/default/default/components/button/button.js';
import cCompanySummary from '#themes/default/default/components/companySummary/companySummary.js';
import cEmailButton from '#themes/default/default/components/emailButton/emailButton.js';
import cHeader from '#themes/default/default/components/header/header.js';
import cInfoBox from '#themes/default/default/components/infoBox/infoBox.js';
import cInstructionStep from '#themes/default/default/components/instructionStep/instructionStep.js';
import cOfflineInstructions from '#themes/default/default/components/offlineInstructions/offlineInstructions.js';
import cOrderSummary from '#themes/default/default/components/orderSummary/orderSummary.js';
import cProduct from '#themes/default/default/components/product/product.js';

import * as omsFrameEvents from '#frontend/util/omsFrameEvents.js';

import template from './thankYou.html' with { type: 'html' };

/**
 * @memberof ThankYouPage
 * @feature Show activation instructions for online payment methods [GEN]
 * @feature If auto-activation product => run auto-activations [GEN]
 * @feature AV Webshield auto-activation (Web only) [GEN]
 * @feature Login into Avast/AVG application using Avast/AVG MyAccount NVAT ticket [GEN]
 * @feature Dynamic instructions (Pricing) [GEN]
 * @feature QR code payment (WireTransfer only) [GEN]
 * @feature Google/Yahoo redirect buttons to the inbox [GEN]
 * @feature Invoice (Offline methods only / Web themes) [GEN]
 */
export default {
	...cartData,
	middleware: [
		invalidCartRedirectMiddleware,
		paymentReturnMiddleware,
		loadOrderMiddleware
	],
	components: {
		cBillingSummary,
		cButton,
		cCompanySummary,
		cEmailButton,
		cHeader,
		cInfoBox,
		cInstructionStep,
		cOfflineInstructions,
		cOrderSummary,
		cProduct
	},
	meta() {
		return {
			title: `${this.t('global_metaTitle')} - ${this.t('thankYou_metaTitle')}`
		};
	},
	computed: {
		isUnverifiedAccount() {
			return this.order.isGuest && this.account && !this.account.verified && this.theme.variables.createAccount;
		},
		isGmail() {
			const email = this.customer.billing.email;
			return email ? RegExp('@gmail.com').test(email) : false;
		},
		isYahoo() {
			const email = this.customer.billing.email;
			return email ? RegExp('@yahoo.com').test(email) : false;
		},
		hasInstructions() {
			return !this.payment.isOffline;
		},
		showOpenEmailButton() {
			return (this.isGmail || this.isYahoo) && this.isUnverifiedAccount;
		},
		showOpenEmailButtonVerified() {
			return (this.isGmail || this.isYahoo) && this.account.verified;
		},
		getHeadline() {
			if (this.payment.isOffline) {
				return this.t('thankYou_offlineInstructionsHeading');
			} else if (this.order.hasTrialProduct) {
				return this.t('thankYou_headingTrial');
			} else {
				return this.t('thankYou_heading');
			}
		},
		getStep2Headline() {
			if (this.order.products.length === 1) {
				return this.t('thankYouInstructions_step2');
			} else {
				return this.t('thankYouInstructions_step2Plural');
			}
		},
		getStep2Text() {
			if (this.order.products.length === 1) {
				return this.t('thankYouInstructions_step2Instructions');
			} else {
				return this.t('thankYouInstructions_step2InstructionsPlural');
			}
		},
		showSontiqSecondStep() {
			return (this.order.hasSontiqProduct || this.order.hasInsuranceProduct) && this.url.locale === 'en-us';
		},
		getLastStepNumber() {
			if (this.isUnverifiedAccount) {
				return this.showSontiqSecondStep ? 3 : 2;
			} else {
				return this.showSontiqSecondStep ? 2 : 1;
			}
		}
	},
	mounted() {
		if (!this.order.isOmsFlow) {
			this.activateProducts({
				order: this.order
			});
		}
		this.forgetForm('customer');
		this.forgetForm('company');
		this.tracking.purchase({
			url: this.url,
			page: this.page,
			order: this.order,
			payment: this.payment,
			theme: this.theme,
			app: this.app,
			account: this.account,
			selectedPayment: this.selectedPayment
		});
		if (this.showOpenEmailButton && this.hasInstructions) {
			this.tracking.emailOpen({
				order: this.order
			}, this.isGmail ? 'gmail' : 'yahoo');
		}
		this.tracking.userData({
			account: this.account,
			theme: this.theme
		});
		if (this.order.isOmsFlow) {
			omsFrameEvents.purchase({
				commerceOrderId: this.order.id
			});
		}
		this.utils.hideLoader();
	},
	beforeRouteLeave(to, from, next) {
		// Don't allow customers to go back when they reach TYP
		next(false);
	},
	methods: {
		openEmail() {
			this.tracking.emailOpenClick({
				order: this.order
			}, this.isGmail ? 'gmail' : 'yahoo');
		}
	},
	template
};
